import React, { createContext, useContext, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { CONSTANT } from '@const';
import { UserTypeContext } from '@context';
import { DqpAccountService, UserAccountService } from '@data';
import { msalService } from '@services';
import validNonRegularUserSideBarListItems from '@specific/sideBarList/sideBarLists/validNonRegularUserSideBarListItems.json';
import validSideBarItems from '@specific/sideBarList/sideBarLists/validSideBarListItems.json';
import { AccountType, DqpAccountType, WaterIQUserType } from '@types';
import { useIsLoggingOut, useLogout, useSessionManager } from '@utils';

import { useMenuItems } from '@/componentsQGDS/navigation/useMenuItems';

export interface AccountContextState {
	getUserAccount: UseQueryResult<AccountType, unknown>;
	getDqpAccount: UseQueryResult<DqpAccountType, unknown>;
	getSelectedAccount: UseQueryResult<AccountType | DqpAccountType, unknown> | undefined;
}

const contextDefaultValues: AccountContextState = {
	getUserAccount: useQuery.prototype,
	getDqpAccount: useQuery.prototype,
	getSelectedAccount: undefined,
};

export const AccountContext = createContext<AccountContextState>(contextDefaultValues);

export const AccountContextProvider = ({ children }: { children: React.ReactNode }) => {
	// Context
	const { selectedUserType } = useContext(UserTypeContext);
	const { menuItems } = useMenuItems();
	// Others
	const logout = useLogout();
	const isLoggingOut = useIsLoggingOut();
	const router = useRouter();
	const pathname = usePathname();
	const { isSessionTimedOut, clearSession } = useSessionManager();

	// Data
	const getUserAccount = useQuery({
		queryKey: [CONSTANT.USERS_API_URL, 0],
		queryFn: () => UserAccountService.getUserAccount(),
		enabled: selectedUserType == WaterIQUserType.WEH,
	});
	const getDqpAccount = useQuery({
		queryKey: [CONSTANT.DQP_USER_API_URL, 0],
		queryFn: () => DqpAccountService.getDqpAccount(),
		enabled: selectedUserType == WaterIQUserType.DQP,
	});

	/**
	 * Internal Navigation Access Control
	 * Manage redirection to allowed page when changing route or selected user type
	 * It pushes the user back to their allowed default url
	 */
	useEffect(() => {
		const currentUrlPath = pathname;

		// Skip this Access Control as logging out is in progress
		if (isLoggingOut) {
			// Force redirect to logout
			if (currentUrlPath !== '/logout') {
				router.push('/logout');
			}
			return;
		}

		const exemptRoutes = ['/trades', '/contact'];
		const isExempt = exemptRoutes.includes(pathname ?? '/never');

		if (isExempt) {
			return;
		}

		if (!msalService.isValidAccount() && !msalService.isValidUnlinkedAccount()) {
			msalService.logAccountValidations();
			if (!msalService.isClosedAccount()) {
				router.push('/invalid-user');
				return;
			} else {
				router.push('/account-closed');
				return;
			}
		} else if (!msalService.validateUserTypes()) {
			msalService.logAccountValidations();
			router.push('/registration');
			return;
		}

		let allowedUrlPaths = ['/'];
		let defaultValidUrlPath = '/';

		// Access control for root path
		if (currentUrlPath === '/') {
			if (msalService.getUserTypes()?.includes(WaterIQUserType.DQP)) {
				router.push(CONSTANT.DQP_HOME_PAGE);
			} else if (msalService.getUserTypes()?.includes(WaterIQUserType.WEH)) {
				router.push('/home');
			}
			return;
		}

		if (!selectedUserType) {
			allowedUrlPaths = ['/registration'];
			defaultValidUrlPath = '/registration';
		} else if (selectedUserType == WaterIQUserType.DQP) {
			allowedUrlPaths = (menuItems ?? []).map(item => item.href);
			// TODO: Remove these example pages when DQP pages have been created in app router
			if (CONSTANT.SHOW_DEV_PAGES) {
				const mvpUrlPaths = (validNonRegularUserSideBarListItems ?? []).map(item => item.url);
				mvpUrlPaths.push('/mvp/playground');
				allowedUrlPaths = allowedUrlPaths.concat(mvpUrlPaths);
				allowedUrlPaths.push('/example1');
				allowedUrlPaths.push('/example2');
			}
			// TODO: --end to remove
			defaultValidUrlPath = CONSTANT.DQP_HOME_PAGE;
		} else if (selectedUserType == WaterIQUserType.WEH) {
			allowedUrlPaths = (validSideBarItems ?? []).map(item => item.url);
			defaultValidUrlPath = '/home';
		}

		const isAllowedUrl = allowedUrlPaths.some(urlPath => {
			// To allow url params
			if (urlPath === CONSTANT.DQP_HOME_PAGE || urlPath === '/devices') {
				return currentUrlPath?.startsWith(urlPath);
			}
			return urlPath === currentUrlPath;
		});

		if (!isAllowedUrl) {
			router.push(defaultValidUrlPath);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname, selectedUserType]); // do not add isLoggingOut as it is not necessary to track its change here

	/**
	 * Logout when session timed out
	 * or force logout when a logging out process is still pending after reload
	 */
	useEffect(() => {
		if (isSessionTimedOut || isLoggingOut) {
			logout();
			clearSession();
		}
	}, [isSessionTimedOut, isLoggingOut]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<AccountContext.Provider
			value={{
				getUserAccount: getUserAccount,
				getDqpAccount: getDqpAccount,
				getSelectedAccount:
					selectedUserType != WaterIQUserType.DQP ? getUserAccount : getDqpAccount,
			}}
		>
			{children}
		</AccountContext.Provider>
	);
};
